// Pricing - Billing Toggle, Seat Adjustment with Animations

import { config, state } from './config.js';

// Animation state tracking
const animationState = new Map();
const moneyFormat = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
const moneyPerSeatFormat = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function formatMoney(value) {
    return moneyFormat.format(value);
}

function formatMoneyPerSeat(value) {
    return moneyPerSeatFormat.format(value);
}

function getBillingLabels() {
    return state.isAnnualBilling
        ? { periodWord: 'year', periodShort: 'yr', seatRate: config.extraSeatPrice.annual }
        : { periodWord: 'month', periodShort: 'mo', seatRate: config.extraSeatPrice.monthly };
}

function updateProfessionalSeatSummary(basePrice, extraCost, totalPrice, totalSeats, baseSeats, maxSeats) {
    const { periodWord, periodShort, seatRate } = getBillingLabels();
    const perSeatCost = totalPrice / totalSeats;

    const seatsNote = document.getElementById('seatsNote');
    if (seatsNote) {
        if (state.extraSeats === 0) {
            seatsNote.textContent = `${baseSeats} seats included in base plan`;
            seatsNote.classList.remove('extra-cost');
        } else {
            seatsNote.textContent = `${totalSeats} seats total (${state.extraSeats} extra, +$${formatMoney(extraCost)}/${periodShort})`;
            seatsNote.classList.add('extra-cost');
        }
    }

    const summary = document.getElementById('professionalBillingSummary');
    if (summary) {
        summary.textContent = `Total $${formatMoney(totalPrice)}/${periodWord} for ${totalSeats} seats ($${formatMoneyPerSeat(perSeatCost)} per seat/${periodWord})`;
    }

    const hint = document.getElementById('professionalSeatHint');
    if (hint) {
        hint.textContent = `Extra seats $${formatMoney(seatRate)} per seat/${periodWord}, up to ${maxSeats}.`;
    }
}

function syncSeatButtons(totalSeats, minSeats, maxSeats) {
    const buttons = document.querySelectorAll('.seats-btn');
    if (buttons.length < 2) return;
    buttons[0].disabled = totalSeats <= minSeats;
    buttons[1].disabled = totalSeats >= maxSeats;
}

/**
 * Animate a numeric value change with counter effect
 * @param {HTMLElement} element - Element to animate
 * @param {number} targetValue - Target numeric value
 * @param {number} duration - Animation duration in ms
 */
function animateValue(element, targetValue, duration = 400) {
    if (!element) return;

    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
        element.textContent = targetValue;
        return;
    }

    // Cancel any existing animation on this element
    const existingAnimation = animationState.get(element);
    if (existingAnimation) {
        cancelAnimationFrame(existingAnimation.rafId);
    }

    const startValue = parseInt(element.textContent) || 0;
    const startTime = performance.now();
    const diff = targetValue - startValue;

    // Add animating class for CSS effects
    element.classList.add('price-animating');

    function update(currentTime) {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Easing function (ease-out cubic)
        const eased = 1 - Math.pow(1 - progress, 3);

        const currentValue = Math.round(startValue + (diff * eased));
        element.textContent = currentValue;

        if (progress < 1) {
            const rafId = requestAnimationFrame(update);
            animationState.set(element, { rafId });
        } else {
            element.textContent = targetValue;
            element.classList.remove('price-animating');
            animationState.delete(element);
        }
    }

    const rafId = requestAnimationFrame(update);
    animationState.set(element, { rafId });
}

/**
 * Toggle billing period (monthly/annual)
 */
export function toggleBilling() {
    const toggle = document.getElementById('billingToggle');
    state.isAnnualBilling = toggle.checked;

    // Update toggle labels
    document.querySelectorAll('.toggle-label').forEach(label => {
        const period = label.dataset.period;
        label.classList.toggle('active',
            (period === 'annual' && state.isAnnualBilling) ||
            (period === 'monthly' && !state.isAnnualBilling)
        );
    });

    // Show/hide save badge with animation
    const saveBadge = document.getElementById('saveBadge');
    if (saveBadge) {
        if (state.isAnnualBilling) {
            saveBadge.style.display = 'inline-block';
            saveBadge.classList.add('badge-pop');
            setTimeout(() => saveBadge.classList.remove('badge-pop'), 300);
        } else {
            saveBadge.style.display = 'none';
        }
    }

    // Update prices with animation (skip Professional - handled separately)
    document.querySelectorAll('.price-amount[data-annual]').forEach(element => {
        if (element.id === 'professionalPrice') return;
        const annual = parseInt(element.dataset.annual);
        const monthly = parseInt(element.dataset.monthly);
        const targetPrice = state.isAnnualBilling ? annual : monthly;
        animateValue(element, targetPrice);
    });

    // Update period text with fade
    document.querySelectorAll('.period-text').forEach(element => {
        element.classList.add('period-switching');
        setTimeout(() => {
            element.textContent = state.isAnnualBilling ? 'year' : 'month';
            element.classList.remove('period-switching');
        }, 150);
    });

    updateProfessionalPrice(true);

    // Sync hidden form input
    const billingInput = document.getElementById('checkoutBillingPeriod');
    if (billingInput) {
        billingInput.value = state.isAnnualBilling ? 'annual' : 'monthly';
    }

}

/**
 * Adjust seats for Professional plan
 */
export function adjustSeats(delta) {
    const professional = config.editions[2];
    const minSeats = professional.baseSeats;
    const maxSeats = professional.maxSeats;

    const newTotalSeats = (minSeats + state.extraSeats) + delta;

    if (newTotalSeats < minSeats || newTotalSeats > maxSeats) {
        return;
    }

    state.extraSeats = newTotalSeats - minSeats;

    // Animate seat count change
    const seatCountEl = document.getElementById('seatCount');
    if (seatCountEl) {
        seatCountEl.classList.add('seat-bump');
        seatCountEl.textContent = newTotalSeats;
        setTimeout(() => seatCountEl.classList.remove('seat-bump'), 200);
    }

    updateProfessionalPrice(true);

    const seatsInput = document.getElementById('checkoutSeats');
    if (seatsInput) {
        seatsInput.value = newTotalSeats;
    }

    const extraSeatsInput = document.getElementById('extraSeats');
    if (extraSeatsInput) {
        extraSeatsInput.value = newTotalSeats;
    }

}

/**
 * Update Professional price display
 * @param {boolean} animate - Whether to animate the change
 */
export function updateProfessionalPrice(animate = false) {
    const professional = config.editions[2];
    const baseSeats = professional.baseSeats;
    const totalSeats = baseSeats + state.extraSeats;
    const basePrice = state.isAnnualBilling ? professional.annualPrice : professional.monthlyPrice;
    const extraCost = state.extraSeats * (state.isAnnualBilling ? config.extraSeatPrice.annual : config.extraSeatPrice.monthly);
    const totalPrice = basePrice + extraCost;

    const priceEl = document.getElementById('professionalPrice');
    if (priceEl) {
        if (animate) {
            animateValue(priceEl, totalPrice);
        } else {
            priceEl.textContent = totalPrice;
        }
    }

    syncSeatButtons(totalSeats, baseSeats, professional.maxSeats);
    updateProfessionalSeatSummary(basePrice, extraCost, totalPrice, totalSeats, baseSeats, professional.maxSeats);
}
